import _ from 'lodash';

import assetDetailsConstants from '../constants/assetHierarchy.constants';
import * as assetDetailsActions from './assetDetails.actions';
import { axiosInstance, ENDPOINT } from '../../../common/constants';
import { handleResponse } from '../../../common/helpers';
import * as hierarchyUtils from '../utils/assetHierarchyUtils';
import * as breadcrumbActions from '../../Menu/actions/breadcrumb.actions';

export const findNode = (nodeId, nodeType, hierarchy, propertyType, propertyValue) => {
  if (_.isEmpty(hierarchy)) return null;
  if (
    (hierarchy.node_type === nodeType && hierarchy.id === nodeId) ||
    (propertyType && hierarchy[propertyType] === propertyValue)
  ) return hierarchy;

  for (let i = 0; i < hierarchy.children.length; i++) {
    const found = findNode(nodeId, nodeType, hierarchy.children[i], propertyType, propertyValue);
    if (found) {
      found.expanded = true;
      hierarchy.children[i].expanded = true;
      return found;
    }
  }
  return null;
};

export const selectNode = (nodeId, nodeType, propertyType = null, propertyValue = null) => (dispatch, getState) => {
  const assetDetails = getState().assetHierarchyReducer;
  const { site_id } = hierarchyUtils.getSelectedPathFromBreadcrumb(getState().breadcrumb);
  const hierarchy = _.cloneDeep(assetDetails.assetInfo.hierarchy);

  // Check if hierarchy is still loading
  if (hierarchy && hierarchy.loading) {
    return null;
  }

  // Check if hierarchy is an array (valid state)
  if (!Array.isArray(hierarchy)) {
    return null;
  }

  for (let i = 0; i < hierarchy.length; i++) {
    if (hierarchy[i].id === site_id) {
      const node = findNode(nodeId, nodeType, hierarchy[i], propertyType, propertyValue);
      nodeId = nodeId || (node && node.id);
      if (!_.isEmpty(node)) {
        const path = [];
        hierarchyUtils.haspath(hierarchy, nodeId, nodeType, path);
        const params = hierarchyUtils.getHierarchyParams(path, nodeId, nodeType);
        hierarchyUtils.expandPathNodes(hierarchy, path);
        dispatch(breadcrumbActions.updateBreadcrumb(params));
      }
      dispatch(refreshHierarchy(hierarchy));
      return nodeId;
    }
  }
  return null;
};

export const toggleNodeExpand = (nodeId, nodeType) => (dispatch, getState) => {
  const assetDetails = getState().assetHierarchyReducer;
  const { site_id } = hierarchyUtils.getSelectedPathFromBreadcrumb(getState().breadcrumb);
  const hierarchy = _.cloneDeep(assetDetails.assetInfo.hierarchy);
  const toggleNodeHelper = (nodeId, nodeType, nodes) => {
    nodes.forEach((node) => {
      if (node.id === nodeId && node.node_type === nodeType) {
        node.expanded = !node.expanded;
        return;
      }
      toggleNodeHelper(nodeId, nodeType, node.children);
    });
  };
  for (let i = 0; i < hierarchy.length; i++) {
    if (hierarchy[i].id === site_id) {
      toggleNodeHelper(nodeId, nodeType, hierarchy[i].children);
    }
  }
  dispatch(refreshHierarchy(hierarchy));
};

export const refreshHierarchy = hierarchy => dispatch =>
  dispatch({ type: assetDetailsConstants.REFRESH_HIERARCHY, hierarchy });

export const deleteArea = areaId => () => handleResponse(
  axiosInstance.delete(ENDPOINT.DELETE_AREA(areaId))
).then(
  res => res,
  (error) => { throw error; }
);

export const editArea = (areaId, data) => () => handleResponse(
  axiosInstance.patch(ENDPOINT.UPDATE_AREA(areaId), data)
).then(
  res => res,
  (error) => { throw error; }
);

export const createArea = data => () => handleResponse(
  axiosInstance.post(ENDPOINT.CREATE_AREA, data)
).then(
  res => res,
  (error) => { throw error; }
);

export const deleteTags = res => (dispatch) => {
  dispatch({ type: assetDetailsConstants.DELETE_TAGS_REQUEST });
  return handleResponse(axiosInstance.delete(ENDPOINT.TAGS, { data: res })).then(
    (data) => {
      dispatch({ type: assetDetailsConstants.DELETE_TAGS_SUCCESS });
      dispatch(assetDetailsActions.getHierarchy(false));
      return data;
    },
    (error) => {
      dispatch({ type: assetDetailsConstants.DELETE_TAGS_FAILURE, error });
      dispatch(assetDetailsActions.getHierarchy(false));
      throw error;
    }
  );
};

export const toggleHierarchyView = () => (dispatch, getState) => {
  const { hierarchyViewPane: { currentWidth, previousWidth } } = getState();
  // Always collapse to 0 when open. A plain swap breaks when previousWidth was
  // corrupted to another open size (e.g. after resize persisted to localStorage).
  const defaultWidth = 275;
  const payload = currentWidth
    ? { currentWidth: 0, previousWidth: currentWidth }
    : { currentWidth: previousWidth || defaultWidth, previousWidth: 0 };
  dispatch({
    type: 'TOGGLE_HIERARCHY_VIEW',
    payload
  });
};

export const updateHierarchyViewPane = width => (dispatch) => {
  dispatch({ type: 'UPDATE_HIERARCHY_VIEW_PANE_WIDTH', payload: { width } });
};

export const fetchSourceSites = (search_key = '', page = 1, per_page = 300, all = false) => () => {
  const params = {
    search_key,
    page,
    per_page,
    all
  };
  return handleResponse(axiosInstance.get(ENDPOINT.GET_ACCOUNTSLIST, { params })).then(
    res => res,
    (error) => { throw error; }
  );
};

export const fetchSourceMachines = sourceAccountId => () => {
  const params = {
    account_id: sourceAccountId,
    page: 1,
    per_page: 1000
  };
  return handleResponse(axiosInstance.get(ENDPOINT.GET_MACHINE_LIST, { params })).then(
    res => res,
    (error) => { throw error; }
  );
};

export const submitCloneToAccount = payload => () => handleResponse(
  axiosInstance.post(ENDPOINT.CLONE_MACHINES_TO_ACCOUNT, payload)
).then(
  res => res,
  (error) => { throw error; }
);

export const getAsyncTaskStatus = taskId => () => handleResponse(
  axiosInstance.get(ENDPOINT.GET_TASK_STATUS(taskId))
).then(
  res => res,
  (error) => { throw error; }
);

export const getCloneTaskStatus = taskId => () => handleResponse(
  axiosInstance.get(ENDPOINT.CLONE_MACHINES_TO_ACCOUNT_STATUS(taskId))
).then(
  res => res,
  (error) => { throw error; }
);
